import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useDarkMode } from "../DarkModeContext";
import DarkModeToggle from "../DarkModeToggle";
import { useSettingsModal } from "../../contexts/SettingsModalContext";
import { useSandbox } from "../../hooks/useSandbox";
import { useSandboxStore } from "../../store/sandboxStore";
import { useAuthenticatedApi } from "../../hooks/useAuthenticatedApi";
import { apiEndpoints } from "../../utils/api";
import { useIsLocalhost } from "../../store/environmentStore";
import HelpSystem from "../tour/HelpSystem";
import ViewPromptsModal from "../admin/ViewPromptsModal";
import {
  Shield,
  LogOut,
  ChevronDown,
  User,
  MessageSquare,
  Code,
  Settings,
  UserCog,
  FolderOpen,
  Menu,
  X,
  FileCode2,
  RefreshCw,
  MoreVertical,
  Moon,
  Sun,
  Bug,
  Upload,
  BookOpen,
  Zap,
} from "lucide-react";
import BugReportButton from "../BugReportButton";
import NotificationIcon from "../NotificationIcon";
import { useDeployments } from "../../hooks/useDeployments";
import { useWorkspaceCommits } from "../../hooks/useWorkspaceCommits";
import { Rocket, Loader2, ExternalLink } from "lucide-react";
import PublishMenu from "../project/PublishMenu";

interface HeaderProps {
  type?: "homepage" | "app";
  showNavigation?: boolean;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
  condition?: boolean;
}

interface RouteInfo {
  workspaceId: string;
  isThreadView: boolean;
  threadId: string;
  activeSection: string;
  projectBasePath: string;
}

interface RunningJob {
  id: string;
  thread_id: string;
  agent_type: string;
  status: string;
  created_at: string;
}

interface RunningJobsResponse {
  has_active_jobs: boolean;
  job_count: number;
  jobs: RunningJob[];
}

const Header: React.FC<HeaderProps> = ({
  type = "app",
  showNavigation = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { user, isAuthenticated, logout, isLoading, isAdmin, displayName } = useAuth();
  const api = useAuthenticatedApi();
  const { openProjectSettings, openAccountSettings } = useSettingsModal();
  const menuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // State management
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const [projectName, setProjectName] = useState<string>("");
  const [workspaceDeployedUrl, setWorkspaceDeployedUrl] = useState<string>("");
  const [workspaceTemplateSlug, setWorkspaceTemplateSlug] = useState<string | undefined>(undefined);
  const isLocalhost = useIsLocalhost();
  const [viewPromptsModalOpen, setViewPromptsModalOpen] = useState(false);
  const [sandboxMenuOpen, setSandboxMenuOpen] = useState(false);
  const [isResettingSandbox, setIsResettingSandbox] = useState(false);
  const [runningJobsInfo, setRunningJobsInfo] =
    useState<RunningJobsResponse | null>(null);
  const [profilePictureError, setProfilePictureError] = useState(false);
  const [isCheckingJobs, setIsCheckingJobs] = useState(false);
  const sandboxMenuRef = useRef<HTMLDivElement>(null);
  const mobileActionsRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Extract route information
  const routeInfo = React.useMemo((): RouteInfo => {
    const pathParts = location.pathname.split("/");
    const hasWorkspaceId =
      pathParts.includes("project") &&
      pathParts[pathParts.indexOf("project") + 1];
    const workspaceId = hasWorkspaceId
      ? pathParts[pathParts.indexOf("project") + 1]
      : "";
    const isThreadView = pathParts.includes("thread");
    const threadId =
      isThreadView && pathParts[pathParts.indexOf("thread") + 1]
        ? pathParts[pathParts.indexOf("thread") + 1]
        : "";

    const projectBasePath = location.pathname.startsWith("/project/")
      ? location.pathname.split(`/project/${workspaceId}/`)[1] || ""
      : "";

    const activeSection = (() => {
      if (isThreadView) {
        const urlParams = new URLSearchParams(location.search);
        const tab = urlParams.get("tab") || "chat";
        return `thread-${tab}`;
      }
      if (projectBasePath === "code") return "main-code";
      if (projectBasePath === "security") return "main-security";
      if (
        projectBasePath === "" ||
        projectBasePath === "chat" ||
        projectBasePath.startsWith("thread/")
      )
        return "main-chat";
      return "";
    })();

    return {
      workspaceId,
      isThreadView,
      threadId,
      activeSection,
      projectBasePath,
    };
  }, [location]);

  // Store the last visited thread for the current workspace in session storage
  useEffect(() => {
    if (routeInfo.workspaceId && routeInfo.isThreadView && routeInfo.threadId) {
      try {
        // Save the last thread ID for this workspace in session storage
        sessionStorage.setItem(
          `lastThreadVisited_${routeInfo.workspaceId}`,
          routeInfo.threadId,
        );
      } catch (err) {
        console.error("Error saving last thread to session storage:", err);
      }
    }
  }, [routeInfo.workspaceId, routeInfo.isThreadView, routeInfo.threadId]);

  // Sandbox hook - must be called unconditionally
  const sandboxHookResult = useSandbox(
    routeInfo.workspaceId || "",
    !!routeInfo.workspaceId,
  ) as any;
  const sandboxStatus = sandboxHookResult?.sandboxStatus?.status || "idle";
  const retrySandbox = sandboxHookResult?.retry || (() => {});
  const resetSandbox = sandboxHookResult?.resetSandbox || (() => {});

  // Development helper - log sandbox debug info (commented out to prevent HMR issues)
  // useEffect(() => {
  //   if (process.env.NODE_ENV === 'development' && routeInfo.workspaceId) {
  //     const debugInfo = useSandboxStore.getState().debugInfo();
  //     console.log('[Header] Sandbox Debug Info:', debugInfo);
  //   }
  // }, [routeInfo.workspaceId]);

  // Navigation items configuration
  const mainNavItems: NavItem[] = React.useMemo(
    () =>
      [
        // { id: 'main-chat', label: 'Chat', icon: MessageSquare, path: `/project/${routeInfo.workspaceId}` },
        {
          id: "main-security",
          label: "Security",
          icon: Shield,
          path: `/project/${routeInfo.workspaceId}/security`,
          condition: isLocalhost,
        },
      ].filter((item) => item.condition),
    [routeInfo.workspaceId, isLocalhost],
  );

  const threadNavItems: NavItem[] = React.useMemo(
    () =>
      [
        {
          id: "thread-chat",
          label: "Chat",
          icon: MessageSquare,
          path: `${location.pathname}?tab=chat`,
          condition: isLocalhost,
        },
      ].filter((item) => item.condition),
    [location.pathname],
  );

  useEffect(() => {
    const fetchWorkspaceName = async () => {
      if (routeInfo.workspaceId) {
        try {
          const response = await api.get(
            `/workspaces/${routeInfo.workspaceId}`,
          );
          const data = response.data || response;
          setProjectName(data.name || "");
          setWorkspaceDeployedUrl(data.deployed_url || "");
          setWorkspaceTemplateSlug(data.template_slug || undefined);
          document.title = data.name
            ? `${data.name} | Kolega Studio`
            : "Kolega Studio";
        } catch (err) {
          console.error("Error fetching workspace name:", err);
        }
      } else {
        document.title = "Kolega Studio";
        setWorkspaceDeployedUrl("");
        setWorkspaceTemplateSlug(undefined);
      }
    };

    fetchWorkspaceName();
  }, [routeInfo.workspaceId, api]);

  // Preload profile picture when user changes
  useEffect(() => {
    setProfilePictureError(false);

    if (user?.picture) {
      const img = new Image();
      img.referrerPolicy = 'no-referrer';
      img.onerror = () => setProfilePictureError(true);
      img.src = user.picture;
    }
  }, [user?.picture]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
      if (
        sandboxMenuRef.current &&
        !sandboxMenuRef.current.contains(event.target as Node)
      ) {
        setSandboxMenuOpen(false);
      }
      if (
        mobileActionsRef.current &&
        !mobileActionsRef.current.contains(event.target as Node)
      ) {
        setMobileActionsOpen(false);
      }
      // publish menu state handled internally in PublishMenu
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Helper functions
  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  const getUserDisplayName = () => displayName() || "User";
  const getUserInitials = () => {
    const name = getUserDisplayName();
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleNavigation = (path: string, isThreadNav: boolean = false) => {
    setTimeout(() => {
      setMobileMenuOpen(false);
      setUserMenuOpen(false);
    }, 100);

    if (isThreadNav && location.pathname.includes("/threads")) {
      const tabMatch = path.match(/[?&]tab=([^&]+)/);
      if (tabMatch) {
        const tab = tabMatch[1];
        const urlParams = new URLSearchParams(location.search);
        urlParams.set("tab", tab);
        navigate(`${location.pathname}?${urlParams.toString()}`);
      } else {
        navigate(path);
      }
    } else {
      navigate(path);
    }
  };

  /**
   * Navigate to the last visited thread for the current workspace
   * Falls back to the main project view if no thread was visited
   */
  const navigateToLastThread = () => {
    if (!routeInfo.workspaceId) return;

    try {
      // Get the last thread ID for this workspace from session storage
      const lastThreadId = sessionStorage.getItem(
        `lastThreadVisited_${routeInfo.workspaceId}`,
      );

      if (lastThreadId) {
        // Navigate to that thread
        navigate(`/project/${routeInfo.workspaceId}/thread/${lastThreadId}`);
      } else {
        // If no thread was visited, navigate to the main project view
        navigate(`/project/${routeInfo.workspaceId}`);
      }
    } catch (err) {
      console.error("Error retrieving last thread from session storage:", err);
      // Fallback to main project view
      navigate(`/project/${routeInfo.workspaceId}`);
    }
  };

  const getSandboxStatusConfig = () => {
    const configs = {
      ready: {
        color: "bg-[#ADDD30]",
        animation: "animate-[pulse_2s_ease-in-out_infinite]",
      },
      loading: {
        color: "bg-amber-400",
        animation: "animate-[pulse_1.5s_ease-in-out_infinite]",
      },
      creating: {
        color: "bg-yellow-500",
        animation: "animate-[spin_1s_linear_infinite]",
      },
      resuming: {
        color: "bg-amber-600",
        animation: "animate-[spin_1s_linear_infinite]",
      },
      sleeping: {
        color: "bg-slate-400",
        animation: "animate-[pulse_3s_ease-in-out_infinite]",
      },
      error: {
        color: "bg-red-600 hover:bg-red-500 hover:shadow-md hover:scale-110",
        animation: "",
      },
    };
    return (
      configs[sandboxStatus as keyof typeof configs] || {
        color: "",
        animation: "",
      }
    );
  };

  // Check for running jobs when the dropdown opens
  const checkRunningJobs = async () => {
    if (!routeInfo.workspaceId) return;

    setIsCheckingJobs(true);
    try {
      const response = await api.get(
        apiEndpoints.workspaceRunningJobs(routeInfo.workspaceId),
      );
      setRunningJobsInfo(response);
    } catch (error) {
      console.error("Failed to check running jobs:", error);
      setRunningJobsInfo(null);
    } finally {
      setIsCheckingJobs(false);
    }
  };

  // Handle sandbox reset
  const handleResetSandbox = async () => {
    if (!routeInfo.workspaceId || isResettingSandbox) return;

    // Check for running jobs first
    if (runningJobsInfo?.has_active_jobs) {
      alert(
        `Cannot reset sandbox while ${runningJobsInfo.job_count} agent job(s) are running. Please wait for all jobs to complete.`,
      );
      return;
    }

    setIsResettingSandbox(true);
    setSandboxMenuOpen(false);

    try {
      // Use the resetSandbox function from the hook which handles store updates
      await resetSandbox();
    } catch (error: any) {
      console.error("Failed to reset sandbox:", error);

      // Show user-friendly error message for job conflicts
      if (error.response?.status === 409) {
        alert(
          error.response.data.detail ||
            "Cannot reset sandbox while agent jobs are running. Please wait for all jobs to complete.",
        );
      } else {
        alert("Failed to reset sandbox. Please try again.");
      }
    } finally {
      setIsResettingSandbox(false);
    }
  };

  // (moved) Don't show header on login page — placed just before JSX return so hooks order is consistent

  // Navigation components
  const NavigationItems = ({
    items,
    isThread = false,
  }: {
    items: NavItem[];
    isThread?: boolean;
  }) => (
    <div className="flex items-center space-x-3">
      {items.map((item) => (
        <div key={item.id} className="relative group">
          <button
            className={`group-item flex items-center px-3 py-1.5 text-sm transition-all duration-200 rounded-md ${
              routeInfo.activeSection === item.id
                ? "text-neutral-800 dark:text-white font-medium"
                : "hover:bg-slate-200 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:text-neutral-800"
            }`}
            onClick={() => handleNavigation(item.path, isThread)}
            data-tour={
              isThread
                ? item.id === "thread-chat"
                  ? "thread-chat-tab"
                  : ""
                : item.id === "main-chat"
                  ? "chat-nav-button"
                  : item.id === "main-code"
                    ? "code-nav-button"
                    : item.id === "main-security"
                      ? "security-nav-button"
                      : ""
            }
          >
            <span className="inline-flex items-center">
              <item.icon size={14} className="mr-1.5" />
              <span className="hidden sm:inline">{item.label}</span>
            </span>
          </button>
          <div
            className={`absolute bottom-0 h-0.5 bg-brand-primaryDark dark:bg-brand-primaryDarkMode transition-all duration-200 ${
              routeInfo.activeSection === item.id
                ? "left-1/2 transform -translate-x-1/2 w-[calc(100%-1.5rem)]"
                : "group-hover:w-full left-0 w-0"
            }`}
          />
        </div>
      ))}
    </div>
  );

  const UserAvatar = React.useMemo(() => {
    if (user?.picture && !profilePictureError) {
      return (
        <div
          className="w-6 sm:w-7 h-6 sm:h-7 rounded-full bg-cover bg-center"
          style={{ backgroundImage: `url(${user.picture})` }}
          title="User profile"
          role="img"
          aria-label="User profile"
        />
      );
    }
    return (
      <div className="w-6 sm:w-7 h-6 sm:h-7 rounded-full flex items-center justify-center bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300" title="User">
        <User size={14} />
      </div>
    );
  }, [user?.picture, profilePictureError]);

  const UserMenu = () => (
    <div className="relative" ref={userMenuRef}>
      <button
        className="flex items-center text-sm px-1.5 sm:px-2.5 py-1.5 rounded-lg transition-all duration-200 hover:bg-slate-400/40 dark:hover:bg-neutral-800 text-neutral-800 dark:text-neutral-200"
        onClick={() => {
          setUserMenuOpen(!userMenuOpen);
          setMobileMenuOpen(false); // Close mobile menu when opening user menu
        }}
        disabled={isLoading}
        data-tour="user-menu-button"
        title="User menu"
      >
        {UserAvatar}
        <span className="hidden sm:inline max-w-32 truncate ml-1.5">
          {isAuthenticated ? getUserDisplayName() : "User"}
        </span>
        <ChevronDown size={12} className="ml-0.5 hidden sm:block" />
      </button>

      {userMenuOpen && (
        <div className="absolute right-0 mt-1 py-1 rounded-lg shadow-lg shadow-neutral-300/30 dark:shadow-black/20 w-52 z-50 bg-slate-100 dark:bg-neutral-900 border border-slate-300 dark:border-neutral-800 text-neutral-800 dark:text-neutral-200 max-h-[calc(100vh-80px)] overflow-y-auto">
          {isAuthenticated && user ? (
            <>
              <div className="px-3 py-2 border-b border-slate-300 dark:border-neutral-800">
                <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                  {displayName() || "User"}
                </div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                  {user.email}
                </div>
              </div>

              {isAdmin && (
                <div className="border-b border-slate-300 dark:border-neutral-800 py-1">
                  <button
                    key="admin-link"
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate("/admin");
                    }}
                    className="flex items-center w-full text-left px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                    title="Administration"
                  >
                    <UserCog size={14} className="mr-1.5" />
                    Administration
                  </button>
                  {routeInfo.workspaceId && (
                    <button
                      key="view-prompts"
                      onClick={() => {
                        setUserMenuOpen(false);
                        setViewPromptsModalOpen(true);
                      }}
                      className="flex items-center w-full text-left px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                      title="View Prompts"
                    >
                      <FileCode2 size={14} className="mr-1.5" />
                      View Prompts
                    </button>
                  )}
                </div>
              )}

              <div className="py-1">
                <button
                  key="account"
                  onClick={() => {
                    setUserMenuOpen(false);
                    openAccountSettings();
                  }}
                  className="flex items-center w-full text-left px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                  title="Account settings"
                >
                  <User size={14} className="mr-1.5" />
                  Account
                </button>
                <div className="border-t border-slate-300 dark:border-neutral-800 pt-1 mt-1">
                  <button
                    key="sign-out"
                    onClick={() => {
                      setUserMenuOpen(false);
                      handleSignOut();
                    }}
                    disabled={isLoading}
                    className="flex items-center w-full text-left px-3 py-2 text-sm text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                    title="Sign out"
                  >
                    <LogOut size={14} className="mr-1.5" />
                    {isLoading ? "Signing out..." : "Sign Out"}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="px-3 py-2">
              <button
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate("/");
                }}
                className="block w-full text-left px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors rounded"
              >
                Sign In
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const MobileMenu = () => {
    if (!mobileMenuOpen) return null;

    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 z-40 lg:hidden bg-black/20"
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* Dropdown Menu - positioned fixed for better visibility */}
        <div className="fixed right-2 sm:right-4 top-14 w-64 lg:hidden z-50">
          <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden max-h-[calc(100vh-80px)] overflow-y-auto">
            {/* Project info and navigation */}
            <div className="py-2">
              {routeInfo.workspaceId && (
                <>
                  <div className="px-4 py-2">
                    <div className="flex items-center text-sm font-medium text-neutral-700 dark:text-neutral-300">
                      <span className="truncate" title={projectName}>{projectName}</span>
                    </div>
                  </div>
                  <div className="h-px bg-neutral-200 dark:bg-neutral-800 my-1" />
                </>
              )}

              {/* Navigation items */}
              {routeInfo.workspaceId && !routeInfo.isThreadView && (
                <div className="px-2">
                  {mainNavItems.map((item) => (
                    <div key={item.id} className="relative mb-1 group">
                      <button
                        onClick={() => {
                          handleNavigation(item.path);
                        }}
                        className={`flex items-center w-full px-3 py-2 text-sm transition-all rounded-md ${
                          routeInfo.activeSection === item.id
                            ? "font-medium text-neutral-800 dark:text-white"
                            : "hover:bg-slate-200 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:text-neutral-800"
                        }`}
                      >
                        <item.icon size={16} className="mr-2" />
                        {item.label}
                      </button>
                      <div
                        className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 h-0.5 bg-brand-primaryDark dark:bg-brand-primaryDarkMode transition-all duration-200 ${
                          routeInfo.activeSection === item.id
                            ? "w-full"
                            : "group-hover:w-full w-0"
                        }`}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Thread navigation */}
              {routeInfo.isThreadView && (
                <div className="px-2">
                  {threadNavItems.map((item) => (
                    <div key={item.id} className="relative mb-1 group">
                      <button
                        onClick={() => {
                          handleNavigation(item.path, true);
                        }}
                        className={`flex items-center w-full px-3 py-2 text-sm transition-all rounded-md ${
                          routeInfo.activeSection === item.id
                            ? "font-medium text-neutral-800 dark:text-white"
                            : "hover:bg-slate-200 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:text-neutral-800"
                        }`}
                      >
                        <item.icon size={16} className="mr-2" />
                        {item.label}
                      </button>
                      <div
                        className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 h-0.5 bg-brand-primaryDark dark:bg-brand-primaryDarkMode transition-all duration-200 ${
                          routeInfo.activeSection === item.id
                            ? "w-full"
                            : "group-hover:w-full w-0"
                        }`}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Mobile-specific action items - only show on mobile */}
              {isMobile && (
                <>
                  <div className="border-t border-slate-300 dark:border-neutral-800 my-1" />
                  
                  {/* Dark Mode Toggle */}
                  <button
                    onClick={() => {
                      toggleDarkMode();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center w-full text-left px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                  >
                    {isDarkMode ? <Sun size={14} className="mr-1.5" /> : <Moon size={14} className="mr-1.5" />}
                    {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                  </button>

                  {/* Help Center */}
                  <a
                    href="https://docs.kolega.studio"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center w-full text-left px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                    title="Help Center"
                    aria-label="Open Help Center"
                  >
                    <BookOpen size={16} className="mr-1.5" />
                  </a>

                  {/* Bug Report */}
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      // Trigger bug report modal
                      const bugReportBtn = document.querySelector('[data-bug-report-trigger]') as HTMLButtonElement;
                      if (bugReportBtn) bugReportBtn.click();
                    }}
                    className="flex items-center w-full text-left px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                  >
                    <Bug size={14} className="mr-1.5" />
                    Report Bug
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  const sandboxConfig = getSandboxStatusConfig();

  // Deployments and commits for Publish control
  // publish logic handled in PublishMenu

  // Don't show header on login page
  if (location.pathname === "/login") return null;

  return (
    <>
    <header className="bg-slate-300 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 text-neutral-900 dark:text-white py-1 px-2 sm:px-4 flex items-center justify-between shadow-sm sticky top-0 z-30 w-full transition-all duration-200">
      {/* Logo Section */}
      <div className="flex items-center flex-1">
        <div
          className="flex items-center cursor-pointer group"
          style={{ marginRight: "4.7rem" }}
          onClick={() => {
            if (type === "homepage") {
              window.scrollTo(0, 0);
            } else {
              navigate("/select-project");
            }
          }}
        >
          <h3 className="text-base sm:text-lg font-bold flex items-center">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="mr-1.5 sm:mr-2"
            >
              <path
                d="M7 8L3 12L7 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M17 8L21 12L17 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10 20L14 4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>Kolega</span>
            <span className="font-light">Studio</span>
          </h3>
        </div>

        {/* Desktop Navigation */}
        {type === "app" && routeInfo.workspaceId && (
          <div className="hidden lg:flex ml-2 sm:ml-4 md:ml-6 pl-1 border-l border-slate-500 dark:border-neutral-700">
            <div className="flex items-center px-3 py-1.5 text-sm mr-3 text-black dark:text-white font-medium">
              <span className="truncate max-w-[200px]" title={projectName}>
                {projectName}
              </span>
            </div>
            {!routeInfo.isThreadView ? (
              <NavigationItems items={mainNavItems} />
            ) : (
              <NavigationItems items={threadNavItems} isThread />
            )}
          </div>
        )}
      </div>

      {/* Right Side Actions */}
      <div className="flex items-center space-x-0.5 sm:space-x-4">
        {/* Help Center */}
        <a
          href="https://docs.kolega.studio"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center px-2 py-1.5 text-sm font-medium rounded transition-all hover:bg-slate-400/40 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100"
          title="Help Center"
          aria-label="Open Help Center"
        >
          <BookOpen size={16} className="mr-1.5" />
          <span className="hidden sm:inline">Help Center</span>
        </a>

        {/* Sandbox Status */}
        {type === "app" &&
          routeInfo.workspaceId &&
          sandboxStatus !== "not_applicable" &&
          sandboxStatus !== "idle" && (
            <div className="relative" ref={sandboxMenuRef}>
              <button
                className="relative group flex items-center cursor-pointer p-1.5 rounded-lg hover:bg-slate-400/40 dark:hover:bg-neutral-800 transition-colors"
                onClick={() => {
                  setSandboxMenuOpen(!sandboxMenuOpen);
                  if (!sandboxMenuOpen) {
                    checkRunningJobs();
                  }
                }}
                disabled={isResettingSandbox}
                role="button"
                aria-label={`Sandbox status: ${sandboxStatus}`}
                title={`Sandbox status: ${sandboxStatus}`}
                aria-expanded={sandboxMenuOpen}
                aria-haspopup="true"
                data-tour="sandbox-status"
              >
                <div
                  className={`w-4 h-4 rounded-full transition-all duration-150 ${sandboxConfig.color} ${sandboxConfig.animation}`}
                />
                {sandboxStatus === "error" && (
                  <>
                    <div className="absolute -inset-1 bg-red-600 bg-opacity-25 rounded-full animate-[pulse_2s_ease-in-out_infinite]" />
                    <div className="absolute -inset-2 bg-red-500 bg-opacity-15 rounded-full animate-[pulse_3s_ease-in-out_infinite_0.5s]" />
                    <div className="absolute -inset-3 bg-red-400 bg-opacity-10 rounded-full animate-[pulse_4s_ease-in-out_infinite_1s]" />
                  </>
                )}
                <ChevronDown
                  size={12}
                  className="ml-1 text-neutral-600 dark:text-neutral-400"
                />
              </button>

              {sandboxMenuOpen && (
                <div className="absolute right-0 mt-1 py-1 rounded-lg shadow-lg shadow-neutral-300/30 dark:shadow-black/20 w-52 z-50 bg-slate-100 dark:bg-neutral-900 border border-slate-300 dark:border-neutral-800 text-neutral-800 dark:text-neutral-200">
                  <div className="px-3 py-2 border-b border-slate-300 dark:border-neutral-800">
                    <div className="text-sm font-medium">Sandbox Status</div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                      {sandboxStatus === "ready" && "Sandbox is running"}
                      {sandboxStatus === "loading" && "Checking sandbox..."}
                      {sandboxStatus === "creating" && "Creating sandbox..."}
                      {sandboxStatus === "resuming" && "Resuming sandbox..."}
                      {sandboxStatus === "sleeping" && "Sandbox is sleeping"}
                      {sandboxStatus === "error" && "Sandbox error"}
                    </div>
                    {isCheckingJobs && (
                      <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                        Checking for running jobs...
                      </div>
                    )}
                    {runningJobsInfo?.has_active_jobs && (
                      <div className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        {runningJobsInfo.job_count} active job
                        {runningJobsInfo.job_count !== 1 ? "s" : ""} running
                      </div>
                    )}
                  </div>

                  <div className="py-1">
                    {sandboxStatus === "error" && (
                      <button
                        onClick={() => {
                          setSandboxMenuOpen(false);
                          retrySandbox();
                        }}
                        className="flex items-center w-full text-left px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                      >
                        <RefreshCw size={14} className="mr-1.5" />
                        Retry Connection
                      </button>
                    )}

                    {sandboxStatus === "sleeping" && (
                      <button
                        onClick={() => {
                          setSandboxMenuOpen(false);
                          retrySandbox();
                        }}
                        className="flex items-center w-full text-left px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                      >
                        <Zap size={14} className="mr-1.5" />
                        Wake Sandbox
                      </button>
                    )}

                    <button
                      onClick={handleResetSandbox}
                      disabled={
                        isResettingSandbox ||
                        runningJobsInfo?.has_active_jobs ||
                        isCheckingJobs
                      }
                      className={`flex items-center w-full text-left px-3 py-2 text-sm transition-colors 
                      ${
                        runningJobsInfo?.has_active_jobs
                          ? "text-neutral-400 dark:text-neutral-500 cursor-not-allowed"
                          : "text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      } disabled:opacity-50`}
                      title={
                        runningJobsInfo?.has_active_jobs
                          ? `Cannot reset while ${runningJobsInfo.job_count} job(s) are running`
                          : "Reset the sandbox environment"
                      }
                    >
                      <RefreshCw
                        size={14}
                        className={`mr-1.5 ${isResettingSandbox ? "animate-spin" : ""}`}
                      />
                      {isResettingSandbox ? "Resetting..." : "Reset Sandbox"}
                    </button>

                    {runningJobsInfo?.has_active_jobs &&
                      runningJobsInfo.jobs.length > 0 && (
                        <div className="border-t border-slate-300 dark:border-neutral-800 mt-1 pt-1">
                          <div className="px-3 py-1 text-xs text-neutral-500 dark:text-neutral-400">
                            Active Jobs:
                          </div>
                          {runningJobsInfo.jobs.slice(0, 3).map((job) => (
                            <div
                              key={job.id}
                              className="px-3 py-1 text-xs text-neutral-600 dark:text-neutral-400"
                            >
                              • {job.agent_type} ({job.status})
                            </div>
                          ))}
                          {runningJobsInfo.jobs.length > 3 && (
                            <div className="px-3 py-1 text-xs text-neutral-500 dark:text-neutral-400">
                              ... and {runningJobsInfo.jobs.length - 3} more
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                </div>
              )}
            </div>
          )}


        {/* Notifications - Always visible */}
        {isAuthenticated && <NotificationIcon />}

        {/* Desktop-only actions */}
        {!isMobile && (
          <>
            {/* Bug Report Button */}
            {type === "app" && <BugReportButton />}

            {/* Dark Mode Toggle */}
            <DarkModeToggle
              isDarkMode={isDarkMode}
              toggleDarkMode={toggleDarkMode}
            />

            {/* Publish Dropdown */}
            {type === "app" && routeInfo.workspaceId && (
              <PublishMenu
                workspaceId={routeInfo.workspaceId}
                deployedUrl={workspaceDeployedUrl}
                templateSlug={workspaceTemplateSlug}
              />
            )}
          </>
        )}

        {/* User Menu - Always visible on both mobile and desktop */}
        {type === "app" && <UserMenu />}

        {/* Mobile Menu Button and Dropdown - only show if there are items to display */}
        {routeInfo.workspaceId && (
          <div className="lg:hidden ml-1" ref={menuRef}>
            <button
              className="p-1.5 sm:p-2 rounded-lg hover:bg-slate-400/40 dark:hover:bg-neutral-800 transition-colors"
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
                setUserMenuOpen(false); // Close user menu when opening mobile menu
              }}
              aria-label="Toggle menu"
              title="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X size={18} className="sm:w-5 sm:h-5" />
              ) : (
                <Menu size={18} className="sm:w-5 sm:h-5" />
              )}
            </button>
            <MobileMenu />
          </div>
        )}
      </div>

      {/* Help System */}
      <HelpSystem pageName="header" />

      {/* View Prompts Modal */}
      {viewPromptsModalOpen && routeInfo.workspaceId && (
        <ViewPromptsModal
          isOpen={viewPromptsModalOpen}
          onClose={() => setViewPromptsModalOpen(false)}
          workspaceId={routeInfo.workspaceId}
        />
      )}
    </header>

    </>
  );
};

export default Header;
